// Exports: GroupBlock
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import clsx from "clsx";
import BulkActionBar from "./BulkActionBar";
import MemberAvatar from "./MemberAvatar";
import TaskBlock from "./TaskBlock";
import {
  getMemberKey,
  isTaskCompleted,
  mergeUniqueMembers,
} from "utils/tasks/taskHelpers";
import { Progress } from "../ui-custom/Progress";

const groupContainsTask = (group, taskId) =>
  Boolean((group?.tasks || []).some((task) => task.id === taskId));

const getTaskOrderValue = (task) => {
  const numericValue = Number(
    task?.torder_index ?? task?.task_order ?? task?.taskOrder,
  );
  return Number.isFinite(numericValue) ? numericValue : null;
};

const hasPersistedTaskOrder = (tasks = []) =>
  tasks.some((task) => getTaskOrderValue(task) !== null);

const GroupBlockComponent = ({
  tg,
  siteMembers,
  taskAuthority,
  currentUserMember,
  editingGroupId,
  editingGroupName,
  onGroupEditStart,
  onGroupEditCancel,
  onGroupEditSubmit,
  bulkEditGroupId,
  setBulkEditGroupId,
  selectedTaskIds,
  setSelectedTaskIds,
  bulkMembers,
  bulkPriority,
  setBulkPriority,
  bulkSaving,
  bulkDeleting,
  onBulkSave,
  onBulkDelete,
  onBulkReset,
  toggleBulkMemberSelection,
  showBulkAssigneeDropdown,
  setShowBulkAssigneeDropdown,
  onToggleGroup,
  onAddTaskClick,
  showAddTaskModal,
  setShowAddTaskModal,
  showAddSubtaskModal,
  setShowAddSubtaskModal,
  handleAddTask,
  handleAddSubtask,
  onAddSubtaskClick,
  setSelectedTaskGroup,
  setSelectedTask,
  setSelectedSubtask,
  getInitials,
  preserveScroll,
  closeAddForms,
  onToggleTask,
  onTaskReorder,
  onSubtaskReorder,
  editingTaskId,
  onTaskEditStart,
  onTaskEditCancel,
  onTaskEditSubmit,
  editingSubId,
  onSubEditStart,
  onSubEditCancel,
  onSubEditSubmit,
  handleTaskDateChange,
  handleSubtaskDateChange,
  onToggleSubComplete,
  onTaskStatusChange,
  onTaskPriorityChange,
  openStatusDropdown,
  setOpenStatusDropdown,
  openPriorityDropdown,
  setOpenPriorityDropdown,
  statusOptions,
  refetchTasks,
  refetchSubtasks,
  mergeGroupAllocatedMembers,
  taskReorderDisabled,
}) => {
  const [taskInputValue, setTaskInputValue] = useState("");
  const [draggingTaskId, setDraggingTaskId] = useState(null);
  const [dropIndicator, setDropIndicator] = useState(null);
  const pendingGroupNameRef = useRef(tg.name);
  const inputRef = useRef(null);
  const draggedTaskIdRef = useRef(null);
  const isSubmittingTaskRef = useRef(false);

  useEffect(() => {
    if (editingGroupId !== tg.id) return;
    pendingGroupNameRef.current = tg.name;
    if (inputRef.current) {
      inputRef.current.value = tg.name;
    }
  }, [editingGroupId, tg.id, tg.name]);

  const ongoing = tg.statusPercentages?.inProgress || 0;
  const complete = tg.statusPercentages?.completed || 0;
  const overdue = tg.statusPercentages?.overdue || 0;
  const notStarted = tg.statusPercentages?.notStarted || 0;
  const high = tg.priorityPercentages?.high || 0;
  const medium = tg.priorityPercentages?.medium || 0;
  const low = tg.priorityPercentages?.low || 0;

  const canManageTasks =
    taskAuthority ||
    Boolean(
      currentUserMember?.is_admin ||
      currentUserMember?.create_taskgroup ||
      String(currentUserMember?.joined_as || "").toLowerCase() === "owner",
    );
  const groupMembers = useMemo(() => {
    const allocatedMembers = Array.isArray(tg?.allocated_members)
      ? tg.allocated_members
      : [];

    if (allocatedMembers.length > 0) {
      return allocatedMembers;
    }

    return mergeUniqueMembers(
      (tg?.tasks || []).flatMap((task) => task?.members || []),
    );
  }, [tg?.allocated_members, tg?.tasks]);
  const orderedTasks = useMemo(() => {
    const tasks = [...(tg.tasks || [])];

    if (hasPersistedTaskOrder(tasks)) {
      return tasks.sort((left, right) => {
        const leftOrder = getTaskOrderValue(left);
        const rightOrder = getTaskOrderValue(right);

        if (leftOrder == null && rightOrder == null) return 0;
        if (leftOrder == null) return 1;
        if (rightOrder == null) return -1;

        return leftOrder - rightOrder;
      });
    }

    return tasks.sort((left, right) => {
      const leftDone = isTaskCompleted(left);
      const rightDone = isTaskCompleted(right);
      if (leftDone && !rightDone) return 1;
      if (!leftDone && rightDone) return -1;
      return 0;
    });
  }, [tg.tasks]);
  const orderedTasksRef = useRef(orderedTasks);
  const canReorderTasks =
    !taskReorderDisabled &&
    typeof onTaskReorder === "function" &&
    orderedTasks.length > 1;

  useEffect(() => {
    orderedTasksRef.current = orderedTasks;
  }, [orderedTasks]);

  const resetTaskDrag = useCallback(() => {
    draggedTaskIdRef.current = null;
    setDraggingTaskId(null);
    setDropIndicator(null);
  }, []);

  useEffect(() => {
    if (!draggedTaskIdRef.current) return;

    const draggedTaskStillExists = orderedTasks.some(
      (task) => String(task.id) === String(draggedTaskIdRef.current),
    );

    if (draggedTaskStillExists) return;
    resetTaskDrag();
  }, [orderedTasks, resetTaskDrag]);

  const submitGroupName = () =>
    preserveScroll(`[data-scroll-anchor="group-${tg.id}"]`, () =>
      onGroupEditSubmit(pendingGroupNameRef.current),
    );

  const handleAddTaskSubmit = async (event) => {
    event.preventDefault();
    const taskName = taskInputValue.trim();
    if (!taskName || isSubmittingTaskRef.current) return;

    isSubmittingTaskRef.current = true;
    await preserveScroll(`[data-scroll-anchor="group-${tg.id}"]`, async () => {
      await handleAddTask({
        name: taskName,
        tgId: tg.id,
        torder_index: orderedTasks.length,
      });
      setTaskInputValue("");
      // setShowAddTaskModal(null);
    });
    isSubmittingTaskRef.current = false;
  };

  const getDropPosition = useCallback((event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    return event.clientY < bounds.top + bounds.height / 2 ? "before" : "after";
  }, []);

  const handleTaskDragStart = useCallback(
    (event, taskId) => {
      if (!canReorderTasks) {
        event.preventDefault();
        return;
      }

      event.stopPropagation();
      draggedTaskIdRef.current = taskId;
      setDraggingTaskId(taskId);
      setDropIndicator(null);

      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("application/x-task-id", String(taskId));
      }
    },
    [canReorderTasks],
  );

  const handleTaskDragOver = useCallback(
    (event, taskId) => {
      const draggedTaskId = draggedTaskIdRef.current;

      if (!canReorderTasks || !draggedTaskId) return;
      if (String(draggedTaskId) === String(taskId)) return;

      event.preventDefault();
      event.stopPropagation();

      const position = getDropPosition(event);
      setDropIndicator((current) =>
        current?.taskId === taskId && current?.position === position
          ? current
          : {
              taskId,
              position,
            },
      );

      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = "move";
      }
    },
    [canReorderTasks, getDropPosition],
  );

  const handleTaskDrop = useCallback(
    (event, targetTaskId) => {
      if (!canReorderTasks) return;

      event.preventDefault();
      event.stopPropagation();

      const draggedTaskId =
        draggedTaskIdRef.current ||
        event.dataTransfer?.getData("application/x-task-id");

      if (!draggedTaskId || String(draggedTaskId) === String(targetTaskId)) {
        resetTaskDrag();
        return;
      }

      const currentTasks = orderedTasksRef.current;
      const sourceIndex = currentTasks.findIndex(
        (task) => String(task.id) === String(draggedTaskId),
      );
      const targetIndex = currentTasks.findIndex(
        (task) => String(task.id) === String(targetTaskId),
      );

      if (sourceIndex === -1 || targetIndex === -1) {
        resetTaskDrag();
        return;
      }

      const position =
        dropIndicator?.taskId === targetTaskId
          ? dropIndicator.position
          : getDropPosition(event);
      const reorderedTasks = [...currentTasks];
      const [movedTask] = reorderedTasks.splice(sourceIndex, 1);
      let insertionIndex = targetIndex;

      if (sourceIndex < targetIndex) {
        insertionIndex -= 1;
      }
      if (position === "after") {
        insertionIndex += 1;
      }

      reorderedTasks.splice(insertionIndex, 0, movedTask);
      onTaskReorder(
        tg.id,
        reorderedTasks.map((task) => task.id),
      );
      resetTaskDrag();
    },
    [
      canReorderTasks,
      dropIndicator,
      getDropPosition,
      onTaskReorder,
      resetTaskDrag,
      tg.id,
    ],
  );

  return (
    <div className="mb-2" data-scroll-anchor={`group-${tg.id}`}>
      <div
        className={`
          flex overflow-visible cursor-pointer rounded-lg group
          ${tg.expanded ? "bg-gray-100" : " hover:bg-slate-300 bg-white"}
        `}
        style={{
          borderLeftColor: tg.expanded ? "transparent" : "#3B0069",
          borderTopWidth: tg.expanded ? "0px" : "1px",
          borderRightWidth: tg.expanded ? "0px" : "1px",
          borderBottomWidth: tg.expanded ? "0px" : "1px",
          borderTopColor: tg.expanded ? "transparent" : "#78797A",
          borderRightColor: tg.expanded ? "transparent" : "#78797A",
          borderBottomColor: tg.expanded ? "transparent" : "#78797A",
          borderLeftWidth: tg.expanded ? "0px" : "4px",
        }}
        onClick={() => {
          setSelectedTaskGroup(tg);
          setSelectedTask(null);
          setSelectedSubtask(null);
        }}
      >
        <div className="flex-grow px-4 py-2 hover:bg-[#ECE9EE]">
          <div className="grid grid-cols-12 gap-1 items-center">
            <div
              className="flex flex-row items-center col-span-3"
              onClick={() => {
                setSelectedTaskGroup(tg);
                setSelectedTask(null);
                setSelectedSubtask(null);
              }}
            >
              <button
                className="mr-2 hover:bg-gray-300 rounded-full"
                onClick={(event) => {
                  event.stopPropagation();
                  onToggleGroup(event, tg.id);
                }}
              >
                <svg
                  className={clsx("w-4 h-4 xl:w-5 xl:h-5 transform", {
                    "rotate-90": tg.expanded,
                  })}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    d="M9 5l7 7-7 7"
                    strokeWidth={2}
                    strokeLinecap="round"
                  />
                </svg>
              </button>

              <div className="flex flex-col w-[80%] items-start justify-between">
                {editingGroupId === tg.id ? (
                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      submitGroupName();
                    }}
                    className="flex items-center"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <input
                      autoFocus
                      ref={inputRef}
                      defaultValue={editingGroupName || tg.name}
                      onChange={(event) => {
                        pendingGroupNameRef.current = event.target.value;
                      }}
                      onBlur={submitGroupName}
                      className="pl-2 text-[16px] xl:text-[18px] mr-2 border border-purple-300 rounded focus:ring-purple-500"
                    />
                    <button
                      type="button"
                      onClick={onGroupEditCancel}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      ✕
                    </button>
                  </form>
                ) : (
                  <span
                    className={`pl-2 truncate w-full xl:text-[18px] text-[16px] px-2 font-medium ${
                      taskAuthority
                        ? "hover:border-[1px] hover:border-purple-600"
                        : ""
                    } rounded-lg cursor-pointer hover:text-purple-600`}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (taskAuthority) {
                        preserveScroll(
                          `[data-scroll-anchor="group-${tg.id}"]`,
                          () => onGroupEditStart(tg),
                        );
                      } else {
                        setSelectedTaskGroup(tg);
                        setSelectedTask(null);
                      }
                    }}
                  >
                    {tg.name}
                  </span>
                )}

                {tg.tasks.length > 0 && tg.expanded && (
                  <div className="ml-2 flex items-center gap-2 w-full mt-1">
                    <Progress
                      value={tg.subtasks_summary.progress_percentage}
                      className="w-24 h-1.5"
                    />
                    <span className="text-[12px] text-gray-500 font-medium">
                      {tg.subtasks_summary.progress_percentage}%
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* {groupMembers.length > 0 && ( */}
            <div className="flex items-center col-span-2">
              {groupMembers.slice(0, 3).map((member, index) => {
                return (
                  <MemberAvatar
                    key={getMemberKey(member, index)}
                    member={member}
                    siteMembers={siteMembers}
                    getInitials={getInitials}
                    className="w-6 h-6 xl:w-8 xl:h-8 rounded-full border-2 border-white"
                    imageClassName="object-cover"
                    fallbackClassName="bg-[#343C6A] text-white font-medium text-[10px] xl:text-xs flex items-center justify-center"
                    style={{ marginLeft: index > 0 ? "-6px" : "0px" }}
                  />
                );
              })}

              {groupMembers.length > 3 && (
                <span
                  className="w-6 h-6 xl:w-8 xl:h-8 rounded-full bg-[#343C6A] text-white text-[10px] xl:text-xs flex items-center justify-center border-2 border-white"
                  style={{ marginLeft: "-6px" }}
                  title={`+${groupMembers.length - 3} more members`}
                >
                  +{groupMembers.length - 3}
                </span>
              )}
            </div>

            {tg.expanded && taskAuthority && (
              <div className="flex flex-row col-span-7 items-center justify-end">
                {bulkEditGroupId === tg.id && (
                  <BulkActionBar
                    siteMembers={siteMembers}
                    bulkMembers={bulkMembers}
                    bulkPriority={bulkPriority}
                    setBulkPriority={setBulkPriority}
                    toggleBulkMemberSelection={toggleBulkMemberSelection}
                    showBulkAssigneeDropdown={showBulkAssigneeDropdown}
                    setShowBulkAssigneeDropdown={setShowBulkAssigneeDropdown}
                    bulkSaving={bulkSaving}
                    bulkDeleting={bulkDeleting}
                    onSave={(event) => {
                      event.stopPropagation();
                      onBulkSave(tg);
                    }}
                    onDelete={(event) => {
                      event.stopPropagation();
                      onBulkDelete(tg);
                    }}
                    onReset={(event) => {
                      event.stopPropagation();
                      onBulkReset();
                    }}
                    getInitials={getInitials}
                  />
                )}

                <button
                  className="ml-3 px-3 py-1 rounded-lg text-sm border hover:bg-gray-100"
                  onClick={(event) => {
                    event.stopPropagation();
                    if (bulkEditGroupId === tg.id) {
                      setBulkEditGroupId(null);
                      setSelectedTaskIds([]);
                      onBulkReset();
                    } else {
                      setBulkEditGroupId(tg.id);
                      setSelectedTaskIds([]);
                    }
                  }}
                  title="Select tasks for bulk actions"
                >
                  {bulkEditGroupId === tg.id ? "Cancel Select" : "Select"}
                </button>
              </div>
            )}

            {!tg.expanded && (
              <>
                <div className="col-span-2 flex flex-col items-center">
                  <span className="text-xs text-gray-500">Status</span>
                  <div className="flex h-4 w-[70%] overflow-hidden rounded-full border mt-1">
                    <div
                      className="bg-yellow-400"
                      style={{ width: `${ongoing}%` }}
                      title={`Ongoing tasks: ${ongoing}%`}
                    />
                    <div
                      className="bg-green-500"
                      style={{ width: `${complete}%` }}
                      title={`Completed tasks: ${complete}%`}
                    />
                    <div
                      className="bg-red-500"
                      style={{ width: `${overdue}%` }}
                      title={`Overdue tasks: ${overdue}%`}
                    />
                    <div
                      className="bg-gray-400"
                      style={{ width: `${notStarted}%` }}
                      title={`Not started tasks: ${notStarted}%`}
                    />
                  </div>
                </div>

                <div className="col-span-2 flex flex-col items-center">
                  <span className="text-xs text-gray-500">Priority</span>
                  <div className="flex h-4 w-[70%] overflow-hidden rounded-full border mt-1">
                    <div
                      className="bg-[#C0B3F3]"
                      title={`Low priority tasks: ${low}%`}
                      style={{ width: `${low}%` }}
                    />
                    <div
                      className="bg-[#8067E0]"
                      style={{ width: `${medium}%` }}
                      title={`Medium priority tasks: ${medium}%`}
                    />
                    <div
                      className="bg-[#2E1B76]"
                      style={{ width: `${high}%` }}
                      title={`High priority tasks: ${high}%`}
                    />
                  </div>
                </div>

                <div className="col-span-3 text-center">
                  <span className="text-xs text-gray-500 block">Progress</span>
                  {/* Progress Bar */}
                  <div className="flex items-center gap-2 w-full mt-1">
                    <Progress
                      value={tg.subtasks_summary.progress_percentage}
                      className="w-24 h-3.5"
                      // greenish green-500
                      color="#3B0069"
                    />
                    {/* <span className="text-[12px] text-gray-500 font-medium">
                      {tg.subtasks_summary.progress_percentage}%
                    </span> */}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {tg.expanded && (
        <div className="mt-1 pl-4 pr-4 py-1 overflow-visible">
          <div
            className="rounded-xl bg-white overflow-visible"
            style={{ borderColor: tg.color || "#3B0069" }}
          >
            <table className="w-full border-none border-0 overflow-visible">
              <thead>
                <tr
                  className="text-black font-normal text-xs xl:text-sm"
                  style={{
                    borderLeftWidth: "6px",
                    borderLeftColor: "#3B0069",
                    borderBottom: "1px solid #78797A",
                    borderTop: "1px solid #78797A",
                    borderRight: "1px solid #78797A",
                  }}
                >
                  {bulkEditGroupId === tg.id && (
                    <th className="py-3 px-0 w-[20px] text-center">
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-[#3B0069]"
                        checked={
                          tg.tasks.length > 0 &&
                          selectedTaskIds.length === tg.tasks.length
                        }
                        onChange={(event) => {
                          if (event.target.checked) {
                            setSelectedTaskIds(tg.tasks.map((task) => task.id));
                          } else {
                            setSelectedTaskIds([]);
                          }
                        }}
                      />
                    </th>
                  )}
                  <th className="py-2 xl:py-3 px-2 xl:px-4 w-[180px] xl:w-[240px] text-center font-normal">
                    Tasks
                  </th>
                  <th className="py-2 xl:py-3 px-2 xl:px-4 w-[40px] xl:w-[60px] text-center font-normal">
                    {" "}
                  </th>
                  <th className="py-2 xl:py-3 px-2 xl:px-4 font-normal text-center w-[150px] xl:w-[200px]">
                    Assignee
                  </th>
                  <th className="py-2 xl:py-3 px-2 xl:px-4 font-normal text-center">
                    Due Date
                  </th>
                  <th className="py-2 xl:py-3 px-2 xl:px-4 font-normal text-center w-[100px] xl:w-[140px]">
                    Status
                  </th>
                  <th className="py-2 xl:py-3 px-2 xl:px-4 font-normal text-center w-[90px] xl:w-[120px]">
                    Priority
                  </th>
                  <th className="py-2 xl:py-3 px-2 xl:px-4 font-normal text-center w-[60px] xl:w-[80px]">
                    Duration
                  </th>
                </tr>
              </thead>

              <tbody>
                {orderedTasks.map((task) => (
                  <TaskBlock
                    key={task.id}
                    tg={tg}
                    task={task}
                    siteMembers={siteMembers}
                    taskAuthority={taskAuthority}
                    bulkEditGroupId={bulkEditGroupId}
                    selectedTaskIds={selectedTaskIds}
                    toggleTaskSelected={(taskId) =>
                      setSelectedTaskIds((current) =>
                        current.includes(taskId)
                          ? current.filter((id) => id !== taskId)
                          : [...current, taskId],
                      )
                    }
                    onToggleTask={onToggleTask}
                    setSelectedTaskGroup={setSelectedTaskGroup}
                    setSelectedTask={setSelectedTask}
                    setSelectedSubtask={setSelectedSubtask}
                    editingTaskId={editingTaskId}
                    onTaskEditStart={onTaskEditStart}
                    onTaskEditCancel={onTaskEditCancel}
                    onTaskEditSubmit={onTaskEditSubmit}
                    editingSubId={editingSubId}
                    onSubEditStart={onSubEditStart}
                    onSubEditCancel={onSubEditCancel}
                    onSubEditSubmit={onSubEditSubmit}
                    showAddSubtaskModal={showAddSubtaskModal}
                    setShowAddSubtaskModal={setShowAddSubtaskModal}
                    setShowAddTaskModal={setShowAddTaskModal}
                    handleAddSubtask={handleAddSubtask}
                    onAddSubtaskClick={onAddSubtaskClick}
                    openStatusDropdown={openStatusDropdown}
                    setOpenStatusDropdown={setOpenStatusDropdown}
                    openPriorityDropdown={openPriorityDropdown}
                    setOpenPriorityDropdown={setOpenPriorityDropdown}
                    statusOptions={statusOptions}
                    onTaskStatusChange={onTaskStatusChange}
                    onTaskPriorityChange={onTaskPriorityChange}
                    handleTaskDateChange={handleTaskDateChange}
                    handleSubtaskDateChange={handleSubtaskDateChange}
                    onToggleSubComplete={onToggleSubComplete}
                    getInitials={getInitials}
                    preserveScroll={preserveScroll}
                    refetchTasks={refetchTasks}
                    refetchSubtasks={refetchSubtasks}
                    mergeGroupAllocatedMembers={mergeGroupAllocatedMembers}
                    closeAddForms={closeAddForms}
                    canReorderTask={canReorderTasks}
                    isDragging={String(draggingTaskId) === String(task.id)}
                    dropIndicatorPosition={
                      dropIndicator?.taskId === task.id
                        ? dropIndicator.position
                        : null
                    }
                    onReorderDragStart={handleTaskDragStart}
                    onReorderDragEnd={resetTaskDrag}
                    onReorderDragOver={handleTaskDragOver}
                    onReorderDrop={handleTaskDrop}
                    onSubtaskReorder={onSubtaskReorder}
                    taskReorderDisabled={taskReorderDisabled}
                  />
                ))}

                {tg.tasks.length === 0 && !taskAuthority && (
                  <tr>
                    <td
                      colSpan={bulkEditGroupId === tg.id ? 7 : 6}
                      className="px-3 py-2 text-base text-gray-700 text-left"
                    >
                      No Tasks
                    </td>
                  </tr>
                )}

                {canManageTasks && bulkEditGroupId !== tg.id && (
                  <tr className="bg-gray-50 border-l-[6px] border-[#3c006976]">
                    {showAddTaskModal === tg.id ? (
                      <td colSpan={7} className="px-4 py-3 text-left text-sm">
                        <form
                          onSubmit={handleAddTaskSubmit}
                          className="flex items-center relative w-full"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <div className="relative flex-1">
                            <input
                              autoFocus
                              type="text"
                              placeholder="New Task Name"
                              maxLength={65}
                              value={taskInputValue}
                              onChange={(event) =>
                                setTaskInputValue(event.target.value)
                              }
                              onKeyDown={(event) => {
                                if (event.key === "Escape") {
                                  setShowAddTaskModal(null);
                                  setTaskInputValue("");
                                }
                              }}
                              className="pl-2 py-1 text-xs xl:text-[16px] mr-2 border border-purple-300 rounded focus:ring-2 focus:ring-purple-500 transition-all duration-200 ease-in-out w-[95%]"
                            />
                            {taskInputValue.length >= 55 && (
                              <div className="absolute -bottom-6 left-0 text-xs text-gray-500">
                                {taskInputValue.length}/65 characters
                              </div>
                            )}
                          </div>
                          <button
                            type="submit"
                            disabled={!taskInputValue.trim()}
                            className={`text-purple-600 hover:text-purple-800 ${
                              !taskInputValue.trim()
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                            }`}
                          >
                            Add
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowAddTaskModal(null);
                              setTaskInputValue("");
                            }}
                            className="text-gray-500 hover:text-gray-700 ml-2"
                          >
                            ✕
                          </button>
                        </form>
                      </td>
                    ) : (
                      <>
                        <td className="px-4 py-3 text-left text-sm">
                          <button
                            onClick={() => {
                              preserveScroll(
                                `[data-scroll-anchor="group-${tg.id}"]`,
                                () => {
                                  onAddTaskClick(tg.id);
                                  setShowAddSubtaskModal(null);
                                },
                              );
                            }}
                            className="text-purple-600 hover:text-purple-800 flex items-center"
                          >
                            ➕ Add Task
                          </button>
                        </td>
                        <td colSpan={6} />
                      </>
                    )}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const areEqual = (previousProps, nextProps) => {
  const groupEditAffected =
    previousProps.editingGroupId === previousProps.tg.id ||
    nextProps.editingGroupId === nextProps.tg.id;
  const bulkAffected =
    previousProps.bulkEditGroupId === previousProps.tg.id ||
    nextProps.bulkEditGroupId === nextProps.tg.id;
  const addTaskAffected =
    previousProps.showAddTaskModal === previousProps.tg.id ||
    nextProps.showAddTaskModal === nextProps.tg.id;
  const subtaskFormAffected =
    groupContainsTask(previousProps.tg, previousProps.showAddSubtaskModal) ||
    groupContainsTask(nextProps.tg, nextProps.showAddSubtaskModal);
  const taskDropdownAffected =
    groupContainsTask(previousProps.tg, previousProps.openStatusDropdown) ||
    groupContainsTask(nextProps.tg, nextProps.openStatusDropdown) ||
    groupContainsTask(previousProps.tg, previousProps.openPriorityDropdown) ||
    groupContainsTask(nextProps.tg, nextProps.openPriorityDropdown);
  const taskEditAffected =
    groupContainsTask(previousProps.tg, previousProps.editingTaskId) ||
    groupContainsTask(nextProps.tg, nextProps.editingTaskId) ||
    groupContainsTask(previousProps.tg, previousProps.editingSubId) ||
    groupContainsTask(nextProps.tg, nextProps.editingSubId);

  return (
    previousProps.tg === nextProps.tg &&
    previousProps.siteMembers === nextProps.siteMembers &&
    previousProps.taskAuthority === nextProps.taskAuthority &&
    previousProps.currentUserMember === nextProps.currentUserMember &&
    previousProps.taskReorderDisabled === nextProps.taskReorderDisabled &&
    (!groupEditAffected ||
      previousProps.editingGroupId === nextProps.editingGroupId) &&
    (!bulkAffected ||
      (previousProps.bulkEditGroupId === nextProps.bulkEditGroupId &&
        previousProps.selectedTaskIds === nextProps.selectedTaskIds &&
        previousProps.bulkMembers === nextProps.bulkMembers &&
        previousProps.bulkPriority === nextProps.bulkPriority &&
        previousProps.bulkSaving === nextProps.bulkSaving &&
        previousProps.bulkDeleting === nextProps.bulkDeleting &&
        previousProps.showBulkAssigneeDropdown ===
          nextProps.showBulkAssigneeDropdown)) &&
    (!addTaskAffected ||
      previousProps.showAddTaskModal === nextProps.showAddTaskModal) &&
    (!subtaskFormAffected ||
      previousProps.showAddSubtaskModal === nextProps.showAddSubtaskModal) &&
    (!taskDropdownAffected ||
      (previousProps.openStatusDropdown === nextProps.openStatusDropdown &&
        previousProps.openPriorityDropdown ===
          nextProps.openPriorityDropdown)) &&
    (!taskEditAffected ||
      (previousProps.editingTaskId === nextProps.editingTaskId &&
        previousProps.editingSubId === nextProps.editingSubId))
  );
};

const GroupBlock = memo(GroupBlockComponent, areEqual);

export default GroupBlock;
